import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  toast,
} from "@devhop/ui";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  posApi,
  type SyncInboxListItem,
  type SyncInboxStatus,
} from "../api/client";
import { useCloseShiftMutation } from "../api/queries";
import {
  listLocalCustomerDraftsForBranch,
  listLocalOrderOperationsForShift,
  listLocalOrdersBySyncStatus,
  listLocalOrdersForBranch,
  listLocalOrdersForShift,
} from "../lib/offline-sync/db";
import { waitForAutoSyncIdle } from "../lib/offline-sync/auto-sync";
import {
  type DrainShiftOfflineOrdersProgress,
  drainShiftOfflineOrders,
} from "../lib/offline-sync/drain";
import {
  formatCustomerDraftDisplay,
  isBlockingAdjustmentForShiftClose,
  isBlockingCustomerDraft,
  isBlockingLocalOrderForShiftClose,
  isCouponInboxConflict,
  isCrossShiftPaidOrderPendingSync,
  isLocalOrderCouponConflict,
} from "../lib/offline-sync/local-first-display-helpers";
import type {
  LocalCustomerDraftRecord,
  LocalOrderOperationRecord,
  LocalOrderOperationType,
  LocalOrderRecord,
} from "../lib/offline-sync/types";
import { printShiftReport } from "../lib/print-shift-report";

type CloseShiftDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shiftId: string;
  branchId: string;
  deviceId: string;
  expectedCashAmount?: number;
  onSuccess?: () => void;
};

type CloseShiftSyncOverview = {
  localPaid: number;
  localSynced: number;
  localQueued: number;
  localSyncing: number;
  localFailed: number;
  localNeedsReview: number;
  serverPending?: number;
  adjustmentQueued: number;
  adjustmentSyncing: number;
  adjustmentFailed: number;
  adjustmentNeedsReview: number;
  localBlocking: LocalOrderRecord[];
  adjustmentBlocking: LocalOrderOperationRecord[];
  serverBlocking: SyncInboxListItem[];
  customerDraftBlocking: LocalCustomerDraftRecord[];
  couponConflictBlocking: LocalOrderRecord[];
  couponInboxBlocking: SyncInboxListItem[];
};

function hasBlockingOrders(overview: CloseShiftSyncOverview): boolean {
  return (
    overview.localBlocking.length > 0 ||
    overview.adjustmentBlocking.length > 0 ||
    overview.serverBlocking.length > 0 ||
    overview.customerDraftBlocking.length > 0 ||
    overview.couponConflictBlocking.length > 0 ||
    overview.couponInboxBlocking.length > 0
  );
}

const SERVER_BLOCKING_STATUSES: SyncInboxStatus[] = [
  "pending",
  "processing",
  "failed",
  "dead",
];

const ADJUSTMENT_OPERATION_TYPES: LocalOrderOperationType[] = [
  "void_order",
  "refund_order",
  "move_table",
  "merge_orders",
];

async function listServerBlockingInbox(params: {
  branchId: string;
  deviceId: string;
  shiftId: string;
}): Promise<SyncInboxListItem[]> {
  const results: SyncInboxListItem[] = [];
  for (const status of SERVER_BLOCKING_STATUSES) {
    let offset = 0;
    const limit = 100;
    while (true) {
      const page = await posApi.listSyncInboxOrders({
        ...params,
        status,
        limit,
        offset,
      });
      results.push(...page.items);
      offset += page.items.length;
      if (offset >= page.total || page.items.length === 0) break;
    }
  }
  return results;
}

async function getCloseShiftSyncOverview(params: {
  branchId: string;
  deviceId: string;
  shiftId: string;
}): Promise<CloseShiftSyncOverview> {
  const [
    allLocalOrders,
    branchOrders,
    queued,
    syncing,
    failed,
    needsReview,
    adjustmentQueued,
    adjustmentSyncing,
    adjustmentFailed,
    adjustmentNeedsReview,
    serverBlocking,
    customerDrafts,
  ] = await Promise.all([
    listLocalOrdersForShift(params.shiftId),
    listLocalOrdersForBranch(params.branchId),
    listLocalOrdersBySyncStatus(params.shiftId, ["queued"]),
    listLocalOrdersBySyncStatus(params.shiftId, ["syncing"]),
    listLocalOrdersBySyncStatus(params.shiftId, ["failed"]),
    listLocalOrdersBySyncStatus(params.shiftId, ["needs_review"]),
    listLocalOrderOperationsForShift(params.shiftId, {
      types: ADJUSTMENT_OPERATION_TYPES,
      syncStatuses: ["queued"],
    }),
    listLocalOrderOperationsForShift(params.shiftId, {
      types: ADJUSTMENT_OPERATION_TYPES,
      syncStatuses: ["syncing"],
    }),
    listLocalOrderOperationsForShift(params.shiftId, {
      types: ADJUSTMENT_OPERATION_TYPES,
      syncStatuses: ["failed"],
    }),
    listLocalOrderOperationsForShift(params.shiftId, {
      types: ADJUSTMENT_OPERATION_TYPES,
      syncStatuses: ["needs_review"],
    }),
    listServerBlockingInbox(params),
    listLocalCustomerDraftsForBranch(params.branchId),
  ]);

  const orderByClientId = new Map(
    branchOrders.map((order) => [order.clientOrderId, order]),
  );
  const crossShiftPaidPending = branchOrders.filter((order) =>
    isCrossShiftPaidOrderPendingSync(order, params.shiftId),
  );

  const localBlocking = [
    ...queued.filter(isBlockingLocalOrderForShiftClose),
    ...syncing.filter(isBlockingLocalOrderForShiftClose),
    ...failed.filter(isBlockingLocalOrderForShiftClose),
    ...crossShiftPaidPending,
  ];
  const adjustmentBlocking = [
    ...adjustmentQueued,
    ...adjustmentSyncing,
    ...adjustmentFailed,
  ].filter((operation) =>
    isBlockingAdjustmentForShiftClose(
      operation,
      orderByClientId.get(operation.clientOrderId),
    ),
  );
  const customerDraftBlocking = customerDrafts.filter(isBlockingCustomerDraft);
  const couponConflictBlocking = needsReview.filter(isLocalOrderCouponConflict);
  const couponInboxBlocking = serverBlocking.filter(isCouponInboxConflict);

  return {
    localPaid: allLocalOrders.filter((order) => order.status === "PAID").length,
    localSynced: allLocalOrders.filter((order) => order.syncStatus === "synced")
      .length,
    localQueued: queued.length,
    localSyncing: syncing.length,
    localFailed: failed.length,
    localNeedsReview: needsReview.length,
    serverPending: serverBlocking.length,
    adjustmentQueued: adjustmentQueued.length,
    adjustmentSyncing: adjustmentSyncing.length,
    adjustmentFailed: adjustmentFailed.length,
    adjustmentNeedsReview: adjustmentNeedsReview.length,
    localBlocking,
    adjustmentBlocking,
    serverBlocking,
    customerDraftBlocking,
    couponConflictBlocking,
    couponInboxBlocking,
  };
}

export function CloseShiftDialog({
  open,
  onOpenChange,
  shiftId,
  branchId,
  deviceId,
  expectedCashAmount: initialExpectedCash,
  onSuccess,
}: CloseShiftDialogProps) {
  const [closingAmount, setClosingAmount] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [expectedCashAmount, setExpectedCashAmount] = useState<number>(
    initialExpectedCash ?? 0,
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] =
    useState<DrainShiftOfflineOrdersProgress | null>(null);
  const [syncOverview, setSyncOverview] =
    useState<CloseShiftSyncOverview | null>(null);
  const closeShiftMutation = useCloseShiftMutation();
  const isBusy = closeShiftMutation.isPending || syncStatus != null;

  // Calculate expected cash when dialog opens
  useEffect(() => {
    if (open && !initialExpectedCash) {
      setIsCalculating(true);
      posApi
        .calculateExpectedCash(shiftId, branchId)
        .then((expected) => {
          setExpectedCashAmount(expected);
        })
        .catch((error) => {
          console.error("Failed to calculate expected cash:", error);
        })
        .finally(() => {
          setIsCalculating(false);
        });
    } else if (open && initialExpectedCash) {
      setExpectedCashAmount(initialExpectedCash);
    }
  }, [open, shiftId, branchId, initialExpectedCash]);

  useEffect(() => {
    if (!open) {
      setSyncOverview(null);
      return;
    }

    let cancelled = false;
    const loadSyncOverview = async () => {
      const overview = await getCloseShiftSyncOverview({
        branchId,
        deviceId,
        shiftId,
      });

      if (cancelled) return;
      setSyncOverview(overview);
    };

    void loadSyncOverview();
    return () => {
      cancelled = true;
    };
  }, [open, shiftId, branchId, deviceId]);

  const variance = useMemo(() => {
    const closing = Number.parseFloat(closingAmount);
    if (Number.isNaN(closing)) return null;
    return closing - expectedCashAmount;
  }, [closingAmount, expectedCashAmount]);

  const handleSubmit = async () => {
    const closing = Number.parseFloat(closingAmount);
    if (Number.isNaN(closing) || closing < 0) {
      toast.error("ຈຳນວນເງິນປິດກະ ຕ້ອງເປັນຕົວເລກທີ່ບໍ່ນ້ອຍກວ່າ 0");
      return;
    }

    try {
      setSyncStatus("ລໍຖ້າ background sync ຈົບ...");
      setSyncProgress(null);
      await waitForAutoSyncIdle();

      setSyncStatus("ກຳລັງ sync ສຸດທ້າຍກ່ອນປິດກະ...");
      const syncResult = await drainShiftOfflineOrders({
        branchId,
        deviceId,
        shiftId,
        onProgress: (progress) => {
          setSyncProgress(progress);
          setSyncStatus(progress.message);
        },
      });

      const latestOverview = await getCloseShiftSyncOverview({
        branchId,
        deviceId,
        shiftId,
      });
      setSyncOverview(latestOverview);

      if (syncResult.failed > 0 || hasBlockingOrders(latestOverview)) {
        const blockingTotal =
          latestOverview.localBlocking.length +
          latestOverview.adjustmentBlocking.length +
          latestOverview.serverBlocking.length +
          latestOverview.customerDraftBlocking.length +
          latestOverview.couponConflictBlocking.length +
          latestOverview.couponInboxBlocking.length;
        toast.error(
          `ຍັງມີ sync ຄ້າງ ${blockingTotal} ລາຍການ ກະລຸນາ retry ຫຼື resolve ກ່ອນປິດກະ`,
        );
        return;
      }
      if (syncResult.synced > 0) {
        toast.success(`sync ${syncResult.synced} order ສຳເລັດ`);
      }

      setSyncStatus("ກຳລັງຄິດໄລ່ເງິນຄາດວ່າໃໝ່...");
      setSyncProgress({
        stage: "finalizing",
        message: "ກຳລັງຄິດໄລ່ເງິນຄາດວ່າໃໝ່...",
        total: syncResult.total,
        accepted: syncResult.synced + syncResult.failed,
        rejected: 0,
        completed: syncResult.synced,
        failed: syncResult.failed,
        pending: 0,
        skipped: syncResult.skipped,
      });
      const latestExpectedCash = await posApi.calculateExpectedCash(
        shiftId,
        branchId,
      );
      setExpectedCashAmount(latestExpectedCash);

      setSyncStatus("ກຳລັງປິດກະ...");
      setSyncProgress((prev) =>
        prev
          ? {
              ...prev,
              stage: "finalizing",
              message: "ກຳລັງປິດກະ...",
            }
          : prev,
      );
      await closeShiftMutation.mutateAsync({
        shiftId,
        branchId,
        closingAmount: closing,
        notes: notes.trim() || undefined,
      });

      try {
        const report = await posApi.getShiftReport(shiftId, branchId);
        printShiftReport(report);
        toast.success("ປິດກະ ສຳເລັດແລ້ວ — ກຳລັງພິມລາຍງານກະ");
      } catch (printErr) {
        toast.success("ປິດກະ ສຳເລັດແລ້ວ");
        toast.error(
          printErr instanceof Error
            ? printErr.message
            : "ເກີດຂໍ້ຜິດພາດໃນການພິມລາຍງານກະ",
        );
      }

      setClosingAmount("");
      setNotes("");
      setSyncProgress(null);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການປິດກະ",
      );
    } finally {
      setSyncStatus(null);
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    setClosingAmount("");
    setNotes("");
    setSyncStatus(null);
    setSyncProgress(null);
    setSyncOverview(null);
    onOpenChange(false);
  };

  const syncStageLabel = syncProgress
    ? {
        loading: "ກວດສອບ",
        no_orders: "ບໍ່ມີ order",
        marking: "ກຽມ sync",
        uploading: "ສົ່ງໄປ server",
        waiting: "worker process",
        finalizing: "ສະຫຼຸບຜົນ",
        done: "ສຳເລັດ",
        failed: "ລົ້ມເຫຼວ",
      }[syncProgress.stage]
    : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ປິດກະ</DialogTitle>
          <DialogDescription>ກະລຸນານັບເງິນຈິງແລະປ້ອນຈຳນວນເງິນທີ່ນັບໄດ້</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="expectedCash">ເງິນທີ່ຄາດວ່າ (Expected Cash)</Label>
            {isCalculating ? (
              <div className="mt-2 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground text-sm">
                  ກຳລັງຄິດໄລ່...
                </span>
              </div>
            ) : (
              <>
                <Input
                  id="expectedCash"
                  type="text"
                  value={expectedCashAmount.toLocaleString("lo-LA")}
                  disabled
                  className="mt-2 bg-muted"
                />
                <p className="mt-1 text-muted-foreground text-xs">
                  ຄິດໄລ່ຈາກ: ເງິນຕົ້ນ + ລາຍໄດ້ເງິນສົດ - ຄືນເງິນສົດ
                </p>
              </>
            )}
          </div>

          <div>
            <Label htmlFor="closingAmount">ເງິນສົດທີ່ນັບໄດ້ (Closing Amount)</Label>
            <Input
              id="closingAmount"
              type="number"
              min="0"
              step="0.01"
              value={closingAmount}
              onChange={(e) => setClosingAmount(e.target.value)}
              placeholder="0.00"
              className="mt-2"
            />
          </div>

          {variance !== null && (
            <div>
              <Label>ຄວາມແຕກແຍງ (Variance)</Label>
              <Input
                type="text"
                value={variance.toLocaleString("lo-LA")}
                disabled
                className={`mt-2 bg-muted ${
                  Math.abs(variance) > 0.01
                    ? "text-destructive"
                    : "text-green-600"
                }`}
              />
              <p className="mt-1 text-muted-foreground text-xs">
                {Math.abs(variance) > 0.01
                  ? "ມີຄວາມແຕກແຍງ ກະລຸນາກວດສອບ"
                  : "ບໍ່ມີຄວາມແຕກແຍງ"}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="notes">ໝາຍເຫດ (Notes)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="ໝາຍເຫດເພີ່ມເຕີມ..."
              className="mt-2"
              rows={3}
            />
          </div>

          {syncOverview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">local paid</div>
                  <div className="font-semibold">{syncOverview.localPaid}</div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">local synced</div>
                  <div className="font-semibold text-green-600">
                    {syncOverview.localSynced}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">local queue</div>
                  <div className="font-semibold">
                    {syncOverview.localQueued + syncOverview.localSyncing}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">local failed</div>
                  <div className="font-semibold text-destructive">
                    {syncOverview.localFailed}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">needs review</div>
                  <div className="font-semibold text-amber-700">
                    {syncOverview.localNeedsReview}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">server inbox</div>
                  <div className="font-semibold text-destructive">
                    {syncOverview.serverPending ?? "-"}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">adjust queue</div>
                  <div className="font-semibold">
                    {syncOverview.adjustmentQueued +
                      syncOverview.adjustmentSyncing}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">adjust failed</div>
                  <div className="font-semibold text-destructive">
                    {syncOverview.adjustmentFailed}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">customer drafts</div>
                  <div className="font-semibold text-amber-700">
                    {syncOverview.customerDraftBlocking.length}
                  </div>
                </div>
                <div className="rounded border bg-muted/40 p-2">
                  <div className="text-muted-foreground">coupon conflict</div>
                  <div className="font-semibold text-destructive">
                    {syncOverview.couponConflictBlocking.length +
                      syncOverview.couponInboxBlocking.length}
                  </div>
                </div>
              </div>

              {hasBlockingOrders(syncOverview) && (
                <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  <div className="font-medium text-destructive">
                    ຍັງປິດກະບໍ່ໄດ້: ມີ order sync ຄ້າງ
                  </div>
                  {syncOverview.localBlocking.length > 0 && (
                    <div>
                      <div className="text-muted-foreground text-xs">
                        Local blocking
                      </div>
                      <ul className="mt-1 space-y-1">
                        {syncOverview.localBlocking.slice(0, 5).map((order) => (
                          <li key={order.clientOrderId} className="text-xs">
                            #{order.clientOrderNo} - {order.syncStatus}
                            {order.shiftId !== shiftId ? " (ກະອື່ນ)" : ""}
                            {order.lastSyncError
                              ? `: ${order.lastSyncError}`
                              : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {syncOverview.localBlocking.length === 0 &&
                    hasBlockingOrders(syncOverview) && (
                      <p className="text-muted-foreground text-xs">
                        ກວດ server inbox, customer draft ຫຼື coupon conflict ດ້ານລຸ່ມ
                      </p>
                    )}
                  {syncOverview.serverBlocking.length > 0 && (
                    <div>
                      <div className="text-muted-foreground text-xs">
                        Server inbox blocking
                      </div>
                      <ul className="mt-1 space-y-1">
                        {syncOverview.serverBlocking.slice(0, 5).map((row) => (
                          <li key={row.id} className="text-xs">
                            {row.clientOrderId} - {row.status}
                            {row.errorMessage ? `: ${row.errorMessage}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {syncOverview.adjustmentBlocking.length > 0 && (
                    <div>
                      <div className="text-muted-foreground text-xs">
                        Adjustment operation blocking
                      </div>
                      <ul className="mt-1 space-y-1">
                        {syncOverview.adjustmentBlocking
                          .slice(0, 5)
                          .map((operation) => (
                            <li key={operation.operationId} className="text-xs">
                              {operation.type} -{" "}
                              {operation.syncStatus ?? "not_ready"} (
                              {operation.clientOrderId})
                              {operation.lastSyncError
                                ? `: ${operation.lastSyncError}`
                                : ""}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {syncOverview.customerDraftBlocking.length > 0 && (
                    <div>
                      <div className="text-muted-foreground text-xs">
                        Customer draft ยังไม่ sync
                      </div>
                      <ul className="mt-1 space-y-1">
                        {syncOverview.customerDraftBlocking
                          .slice(0, 5)
                          .map((draft) => (
                            <li key={draft.localCustomerId} className="text-xs">
                              {formatCustomerDraftDisplay(draft)} -{" "}
                              {draft.syncStatus}
                              {draft.lastSyncError
                                ? `: ${draft.lastSyncError}`
                                : ""}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {(syncOverview.couponConflictBlocking.length > 0 ||
                    syncOverview.couponInboxBlocking.length > 0) && (
                    <div>
                      <div className="text-muted-foreground text-xs">
                        Coupon replay conflict
                      </div>
                      <ul className="mt-1 space-y-1">
                        {syncOverview.couponConflictBlocking
                          .slice(0, 3)
                          .map((order) => (
                            <li key={order.clientOrderId} className="text-xs">
                              #{order.clientOrderNo} local -{" "}
                              {order.lastSyncError ?? "needs review"}
                            </li>
                          ))}
                        {syncOverview.couponInboxBlocking
                          .slice(0, 3)
                          .map((row) => (
                            <li key={row.id} className="text-xs">
                              {row.clientOrderId} inbox -{" "}
                              {row.errorCode ?? row.errorMessage ?? row.status}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    ໄປທີ່ Order detail ຫຼື Sync debug ເພື່ອ retry/resolve ກ່ອນປິດກະ
                  </p>
                </div>
              )}
            </div>
          )}

          {syncStatus && (
            <div className="space-y-3 rounded-md border bg-muted/40 p-3 text-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div>
                  <div className="font-medium">
                    {syncStageLabel ? `${syncStageLabel}: ` : ""}
                    {syncStatus}
                  </div>
                  {syncProgress?.jobId && (
                    <div className="text-muted-foreground text-xs">
                      Job ID: {syncProgress.jobId}
                    </div>
                  )}
                </div>
              </div>

              {syncProgress && (
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ທັງໝົດ</div>
                    <div className="font-semibold">{syncProgress.total}</div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">server ຮັບ</div>
                    <div className="font-semibold">{syncProgress.accepted}</div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ສຳເລັດ</div>
                    <div className="font-semibold text-green-600">
                      {syncProgress.completed}
                    </div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ລໍຖ້າ</div>
                    <div className="font-semibold">{syncProgress.pending}</div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ຖືກປະຕິເສດ</div>
                    <div className="font-semibold text-destructive">
                      {syncProgress.rejected}
                    </div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ລົ້ມເຫຼວ</div>
                    <div className="font-semibold text-destructive">
                      {syncProgress.failed}
                    </div>
                  </div>
                  <div className="rounded border bg-background p-2">
                    <div className="text-muted-foreground">ຂ້າມ</div>
                    <div className="font-semibold">{syncProgress.skipped}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isBusy}>
            ຍົກເລີກ
          </Button>
          <Button onClick={handleSubmit} disabled={isBusy || !closingAmount}>
            {isBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {syncStatus ?? "ກຳລັງປິດ..."}
              </>
            ) : (
              "ປິດກະ"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
